import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import ConditionDetailModal from './ConditionDetailModal';

const ConditionsSection = ({ onBookClick }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedConditionId, setSelectedConditionId] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const sliderRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  // Slug mapping for URL updates
  const CONDITION_SLUGS = {
    1: 'kronicne-bolecine',
    2: 'pooperativna-rehabilitacija',
    3: 'sportne-poskodbe',
    4: 'preventiva-mobilnost',
    5: 'individualna-vadba',
    6: 'drza-svetovanje'
  };

  const conditions = [
    {
      id: 1,
      title: 'Kronične bolečine',
      description: 'Bolečine v hrbtu, vratu, sklepih in mišicah',
      image: '/images/bolecine-v-hrbtu-fizioterapija.png',
      color: 'from-red-500 to-rose-400',
      bgColor: 'bg-red-50',
      scale: 'scale-150'
    },
    {
      id: 2,
      title: 'Pooperativna rehabilitacija',
      description: 'Okrevanje po ortopedskih in drugih operacijah',
      image: '/images/sportne-poskodbe-rehabilitacija.png',
      color: 'from-blue-500 to-cyan-400',
      bgColor: 'bg-blue-50',
      scale: 'scale-150'
    },
    {
      id: 3,
      title: 'Športne poškodbe',
      description: 'Zvini, nategi, pretrgane vezi, mišične poškodbe',
      image: '/images/bolecine-v-vratu-terapija.png',
      color: 'from-amber-500 to-yellow-400',
      bgColor: 'bg-amber-50',
      scale: 'scale-150'
    },
    {
      id: 4,
      title: 'Preventiva in mobilnost',
      description: 'Ohranjanje gibljivosti in preprečevanje poškodb',
      image: '/images/rehabilitacija-po-operaciji.png',
      color: 'from-emerald-500 to-green-400',
      bgColor: 'bg-emerald-50',
      scale: 'scale-150'
    },
    {
      id: 5,
      title: 'Individualna vadba',
      description: 'Prilagojene vaje za vaše specifične potrebe',
      image: '/images/glavobol-migrene-zdravljenje.png',
      color: 'from-violet-500 to-purple-400',
      bgColor: 'bg-violet-50',
      scale: 'scale-150'
    },
    {
      id: 6,
      title: 'Drža in svetovanje',
      description: 'Korekcija drže in ergonomsko svetovanje',
      image: '/images/bolecine-v-kolenu-terapija.png',
      color: 'from-[#f87171] to-[#fb923c]',
      bgColor: 'bg-orange-50',
      scale: 'scale-150'
    }
  ];

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const scrollToIndex = (index) => {
    if (sliderRef.current) {
      const cardWidth = sliderRef.current.children[0]?.offsetWidth || 280;
      const gap = 16;
      sliderRef.current.scrollTo({
        left: index * (cardWidth + gap),
        behavior: 'smooth'
      });
      setActiveIndex(index);
    }
  };

  const handleScroll = () => {
    if (sliderRef.current) {
      const cardWidth = sliderRef.current.children[0]?.offsetWidth || 280;
      const gap = 16;
      const scrollLeft = sliderRef.current.scrollLeft;
      const newIndex = Math.round(scrollLeft / (cardWidth + gap));
      if (newIndex !== activeIndex && newIndex >= 0 && newIndex < conditions.length) {
        setActiveIndex(newIndex);
      }
    }
  };

  const openDetailModal = (condition) => {
    setSelectedConditionId(condition.id);
    setSelectedImage(condition.image || null);
    // Update URL without triggering React Router navigation
    const slug = CONDITION_SLUGS[condition.id];
    if (slug) {
      window.history.pushState({ modal: true, conditionId: condition.id }, '', `/${slug}`);
    }
  };

  const closeDetailModal = () => {
    setSelectedConditionId(null);
    setSelectedImage(null);
    // Restore homepage URL
    if (window.location.pathname !== '/') {
      window.history.pushState({}, '', '/');
    }
  };

  // Handle browser back button to close modal
  useEffect(() => {
    const handlePopState = (e) => {
      if (selectedConditionId) {
        setSelectedConditionId(null);
        setSelectedImage(null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [selectedConditionId]);

  return (
    <section 
      id="stanja" 
      className="py-8 md:py-16 bg-gradient-to-b from-white to-slate-50 overflow-hidden"
      data-testid="conditions-section"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-8 md:mb-16 px-4">
          <span className="inline-block text-[#f87171] font-semibold tracking-wider uppercase text-sm mb-3">
            Področja obravnave
          </span>
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-[#1a365d] mb-3">
            Kako vam lahko pomagamo?
          </h2>
          <p className="text-slate-600 text-base md:text-lg max-w-2xl mx-auto">
            Strokovna obravnava za širok spekter težav in stanj
          </p>
        </div>

        {/* Mobile Slider */}
        <div className="md:hidden">
          {/* Slider Container */}
          <div 
            ref={sliderRef}
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide px-4 pb-4"
            onScroll={handleScroll}
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
          >
            {conditions.map((condition, index) => (
              <div
                key={condition.id}
                className={`flex-shrink-0 w-[280px] snap-center ${condition.bgColor} rounded-3xl p-6 transition-all duration-300 ${activeIndex === index ? 'scale-100 shadow-lg' : 'scale-95 opacity-80'}`}
                data-testid={`condition-mobile-${condition.id}`}
              >
                {/* Icon or Image */}
                <div className="flex justify-center mb-5">
                  <div className="w-24 h-24 flex items-center justify-center overflow-visible">
                    <img loading="lazy" decoding="async" 
                      src={condition.image} 
                      alt={`${condition.title} fizioterapija Ljubljana`}
                      className={`w-36 h-36 object-contain ${condition.scale || ''}`}
                    />
                  </div>
                </div>
                
                {/* Content */}
                <h3 className="font-bold text-[#1a365d] text-lg mb-2 text-center">
                  {condition.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed text-center">
                  {condition.description}
                </p>

                {/* CTA Button */}
                <button 
                  onClick={(e) => { e.stopPropagation(); openDetailModal(condition); }}
                  className="w-full mt-4 py-3 bg-white rounded-xl text-[#1a365d] font-semibold text-sm shadow-sm hover:shadow-md transition-all"
                  data-testid={`condition-info-btn-mobile-${condition.id}`}
                >
                  Več informacij →
                </button>
              </div>
            ))}
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-4">
            {conditions.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToIndex(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  activeIndex === index 
                    ? 'w-6 bg-[#f87171]' 
                    : 'bg-slate-300 hover:bg-slate-400'
                }`}
                data-testid={`dot-${index}`}
              />
            ))}
          </div>

          {/* Arrow Navigation */}
          <div className="flex justify-center gap-4 mt-4">
            <button
              onClick={() => scrollToIndex(Math.max(0, activeIndex - 1))}
              disabled={activeIndex === 0}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                activeIndex === 0 
                  ? 'bg-slate-100 text-slate-300' 
                  : 'bg-white shadow-md text-[#1a365d] active:scale-95'
              }`}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => scrollToIndex(Math.min(conditions.length - 1, activeIndex + 1))}
              disabled={activeIndex === conditions.length - 1}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                activeIndex === conditions.length - 1 
                  ? 'bg-slate-100 text-slate-300' 
                  : 'bg-white shadow-md text-[#1a365d] active:scale-95'
              }`}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Desktop Grid */}
        <div className="hidden md:grid grid-cols-3 gap-6 px-4 sm:px-6 lg:px-8">
          {conditions.map((condition) => (
            <div
              key={condition.id}
              className="group relative bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer border border-slate-100 hover:border-[#f87171]/30 overflow-hidden"
              onClick={() => openDetailModal(condition)}
              data-testid={`condition-${condition.id}`}
            >
              {/* Background gradient on hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${condition.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
              
              {/* Icon or Image */}
              <div className="w-24 h-24 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300 overflow-visible">
                <img loading="lazy" decoding="async" 
                  src={condition.image} 
                  alt={`${condition.title} fizioterapija Ljubljana`}
                  className={`w-full h-full object-contain drop-shadow-sm ${condition.scale || ''}`}
                />
              </div>
              
              {/* Content */}
              <h3 className="font-bold text-[#1a365d] text-lg mb-2 group-hover:text-[#f87171] transition-colors">
                {condition.title}
              </h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                {condition.description}
              </p>

              {/* Arrow indicator */}
              <div className="absolute bottom-6 right-6 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-2 group-hover:translate-x-0">
                <svg className="w-4 h-4 text-[#f87171]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          ))}
        </div>


      </div>

      {/* Condition Detail Modal */}
      {selectedConditionId && (
        <ConditionDetailModal
          conditionId={selectedConditionId}
          image={selectedImage}
          onClose={closeDetailModal}
          onBook={onBookClick}
        />
      )}
    </section>
  );
};

export default ConditionsSection;
